import {
  ArrowsClockwise, CheckCircle, Eye, EyeSlash, LockSimple, ShieldCheck,
  Storefront, UserCircle, WarningCircle,
} from '@phosphor-icons/react'
import { useCallback, useEffect, useRef, useState } from 'react'
import { Navigate, useLocation, useNavigate } from 'react-router-dom'
import { request } from '../api/client'
import { useAuth } from '../auth/AuthContext'

export function LoginPage() {
  const { session, login, completeLogin } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const [username, setUsername] = useState(() => localStorage.getItem('leshe-admin-remembered-account') || '')
  const [password, setPassword] = useState('')
  const [showPassword, setShowPassword] = useState(false)
  const [remember, setRemember] = useState(true)
  const [agreed, setAgreed] = useState(true)
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState('')
  const [pending, setPending] = useState<{ id: string; token: string; message?: string } | null>(null)
  const credentials = useRef({ username: '', password: '' })
  const notice = (location.state as { notice?: string } | null)?.notice

  const handleResult = useCallback((result: Awaited<ReturnType<typeof login>>) => {
    if (result.loginStatus === 'AUTHORIZED') {
      completeLogin(result)
      const next = (location.state as { from?: string } | null)?.from || (result.recoveryMode ? '/settings' : '/dashboard')
      navigate(next, { replace: true })
      return
    }
    if (result.loginStatus === 'DEVICE_PENDING' && result.deviceRequestId && result.deviceRequestToken) {
      setPending({ id: String(result.deviceRequestId), token: result.deviceRequestToken, message: result.loginMessage })
      return
    }
    if (result.loginStatus === 'SYSTEM_CLOSED' || result.errorCode === 1070870000) {
      setError('系统已关闭。仅租户老板可从 Web 恢复系统。')
      return
    }
    setError(result.loginMessage || '当前设备未通过授权')
  }, [completeLogin, location.state, navigate])

  const submit = async (event?: React.FormEvent) => {
    event?.preventDefault()
    if (!username.trim() || !password) return setError('请输入账号和密码')
    if (!agreed) return setError('请先阅读并同意用户协议和隐私政策')
    setSubmitting(true); setError(''); credentials.current = { username: username.trim(), password }
    try {
      if (remember) localStorage.setItem('leshe-admin-remembered-account', username.trim())
      else localStorage.removeItem('leshe-admin-remembered-account')
      handleResult(await login(credentials.current))
    }
    catch (reason) { setError(reason instanceof Error ? reason.message : '登录失败') }
    finally { setSubmitting(false) }
  }

  useEffect(() => {
    if (!pending) return
    const poll = async () => {
      try {
        const status = await request<string>({ url: '/luxury/device/request/status', params: { requestId: pending.id, token: pending.token } })
        if (status === 'APPROVED') {
          window.clearInterval(timer)
          handleResult(await login(credentials.current))
        } else if (status === 'REJECTED' || status === 'EXPIRED') {
          window.clearInterval(timer); setPending(null); setError(status === 'REJECTED' ? '设备授权已被拒绝' : '授权申请已过期，请重新登录')
        }
      } catch { /* polling remains resilient */ }
    }
    const timer = window.setInterval(() => { void poll() }, 3000)
    return () => window.clearInterval(timer)
  }, [pending, login, handleResult])

  if (session) return <Navigate to={session.recoveryMode ? '/settings' : '/dashboard'} replace />
  return <main className="scene-page">
    <div className="scene-color" aria-hidden="true" />
    <img className="scene-art" src="/assets/login-data-core.png" alt="" aria-hidden="true" />
    <header className="scene-brand">
      <span className="scene-logo-frame"><img src="/assets/lekeshe-logo.png" alt="" /></span>
      <span className="scene-brand-copy"><strong>乐客奢</strong><small>LEKESHE ERP</small></span>
    </header>
    <section className="scene-copy" aria-label="乐客奢品牌介绍">
      <h1>二奢生意<br />必备管家</h1>
      <p>把商品、订单与经营信息收进一套清晰的门店工作流。</p>
      <ul><li><ArrowsClockwise size={19} />全链路流转</li><li><Storefront size={19} />门店协同</li><li><ShieldCheck size={19} />数据安全自主</li></ul>
    </section>
    <section className="scene-login-card" aria-labelledby="login-title">
      <header className="card-heading"><span className="workspace-mark"><CheckCircle size={18} weight="fill" /></span><span className="workspace-copy">商家工作台</span><h2 id="login-title">登录乐客奢</h2><p>使用门店账号进入经营首页</p></header>
      <form className="scene-form" noValidate onSubmit={submit}>
        <label className="scene-field"><span>账号 / 手机号</span><span className="scene-input-shell"><UserCircle size={21} /><input autoFocus autoComplete="username" value={username} onChange={(event) => setUsername(event.target.value)} placeholder="请输入账号或手机号" /></span></label>
        <label className="scene-field"><span>密码</span><span className="scene-input-shell"><LockSimple size={20} /><input type={showPassword ? 'text' : 'password'} autoComplete="current-password" value={password} onChange={(event) => setPassword(event.target.value)} placeholder="请输入登录密码" /><button className="scene-password-toggle" type="button" onClick={() => setShowPassword((value) => !value)} aria-label={showPassword ? '隐藏密码' : '显示密码'}>{showPassword ? <EyeSlash size={22} /> : <Eye size={22} />}</button></span></label>
        <div className="scene-options"><label className="scene-check"><input type="checkbox" checked={remember} onChange={(event) => setRemember(event.target.checked)} /><span>记住账号</span></label><button type="button" onClick={() => setError('请联系门店管理员重置登录密码')}>忘记密码？</button></div>
        <div className={`scene-notice${error || notice || pending ? ' is-visible' : ''}`} role="status"><WarningCircle size={17} weight="fill" /><span>{pending ? pending.message || '等待管理员授权此设备' : error || notice || '提示信息'}</span></div>
        <button className="scene-submit" disabled={submitting || Boolean(pending)}>{submitting ? '正在验证…' : pending ? '等待设备授权' : '登录'}</button>
        <p className="scene-trial">还没有账号？<button type="button" onClick={() => setError('请在移动端申请试用，或联系乐客奢服务顾问')}>申请试用</button></p>
        <label className="scene-agreement"><input type="checkbox" checked={agreed} onChange={(event) => setAgreed(event.target.checked)} /><span>登录即代表同意<button type="button">《用户协议》</button>和<button type="button">《隐私政策》</button></span></label>
      </form>
    </section>
    <footer className="scene-footer">乐客奢 ERP · 二奢门店经营管理</footer>
  </main>
}
